import React from 'react';
import { motion } from 'framer-motion';
import { useContent } from '../content/ContentContext';
import { Lines } from '../lib/ui';

const ease = [0.2, 0.7, 0.2, 1] as const;

const Hero: React.FC = () => {
  const { hero, platforms } = useContent();
  if (!hero.visible) return null;
  const [first, second] = platforms.items;

  return (
    <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 md:pb-24">
      {/* Ein einziger, ruhiger Lichtakzent hinter der Headline */}
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[520px] w-[900px] -translate-x-1/2 -translate-y-1/3 rounded-full opacity-[0.16] blur-3xl" style={{ background: 'radial-gradient(closest-side, #3fced1, transparent 70%)' }} />

      <div className="container-site">
        <div className="max-w-4xl">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease }} className="eyebrow mb-6">
            {hero.eyebrow}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05, ease }} className="display-xl">
            <Lines text={hero.headline} />
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15, ease }} className="lede mt-7 max-w-2xl">
            {hero.subline}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25, ease }} className="mt-9 flex flex-wrap items-center gap-3">
            <a href={hero.primaryCta.href} className="btn-primary">{hero.primaryCta.label}</a>
            <a href={hero.secondaryCta.href} className="btn-secondary">{hero.secondaryCta.label}</a>
          </motion.div>
        </div>

        {/* Produkt-Showcase: die beiden Plattformen als große Screens, gleich breit */}
        {first && second && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35, ease }}
            className="relative mt-16 grid gap-10 sm:mt-20 md:grid-cols-2 md:gap-5"
          >
            {[first, second].map((p) => (
              <a key={p.id} href="#plattformen" className="group relative overflow-hidden md:card md:card-hover">
                <div className="flex items-center justify-between gap-3 md:px-6 md:pt-6">
                  <div className="flex min-w-0 items-center gap-3">
                    <img src={p.icon} alt="" className="h-8 w-8 shrink-0 rounded-lg" />
                    <span className="truncate text-[15px] font-medium">{p.name}</span>
                  </div>
                  <span className="chip shrink-0">{p.badge}</span>
                </div>
                <div className="mt-4 md:mt-5 md:px-6">
                  <div className="screen-frame rounded-2xl border-b md:rounded-b-none md:border-b-0">
                    <img src={p.image} alt={`${p.name} Screenshot`} className="mt-8 block aspect-[2/1] w-full object-cover object-top transition duration-700 group-hover:scale-[1.015]" loading="eager" />
                  </div>
                </div>
              </a>
            ))}
          </motion.div>
        )}

        {hero.stats.length > 0 && (
          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-14 grid grid-cols-2 gap-x-8 gap-y-8 border-t border-line pt-10 md:grid-cols-4"
          >
            {hero.stats.map((s) => (
              <div key={s.label}>
                <dt className="order-2 text-[13px] text-fg-3">{s.label}</dt>
                <dd className="text-3xl font-semibold tracking-tight text-fg sm:text-4xl">{s.value}</dd>
              </div>
            ))}
          </motion.dl>
        )}
      </div>
    </section>
  );
};

export default Hero;
